import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext.js";
import AfeService from "../service/afeService.js";
import Q8NoData from "../components/Q8Nodata.js";

const AfeChangeHistory = () => {
  // State
  const [changeHistory, setChangeHistory] = useState([]);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [loading, _] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    code: "",
  });

  const { user, bestRole } = useContext(UserContext);

  // Helper functions
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const toggleExpandRecord = (id) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadChangeHistory = async () => {
    try {
      const response = await AfeService.getRecordChangeHistory(filters);

      // Parse JSON strings to objects
      const processedChanges = response.map((change) => {
        let originalRecord = {};
        let modifiedRecord = {};

        try {
          originalRecord = JSON.parse(change.originalRecordJson || "{}");
        } catch (e) {
          console.error("Error parsing originalRecordJson:", e);
        }

        try {
          modifiedRecord = JSON.parse(change.modifiedRecordJson || "{}");
        } catch (e) {
          console.error("Error parsing modifiedRecordJson:", e);
        }

        return {
          ...change,
          originalRecord,
          modifiedRecord,
        };
      });

      setChangeHistory(processedChanges);
    } catch (error) {
      console.error("Error loading change history:", error);
      showNotification("Error loading history", "error");
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    loadChangeHistory();
  }, [filters]);

  const handleFilterChange = (key) => (e) => {
    setFilters({
      ...filters,
      [key]: e.target.value,
    });
  };

  // Function to detect and highlight differences between original and modified records
  const isDifferent = (key, originalRecord, modifiedRecord) => {
    if (!originalRecord || !modifiedRecord) return false;

    // Converti null e undefined in stringhe vuote per un confronto uniforme
    const originalValue =
      originalRecord[key] === null || originalRecord[key] === undefined
        ? ""
        : typeof originalRecord[key] === "number"
        ? originalRecord[key].toString()
        : originalRecord[key];

    const modifiedValue =
      modifiedRecord[key] === null || modifiedRecord[key] === undefined
        ? ""
        : typeof modifiedRecord[key] === "number"
        ? modifiedRecord[key].toString()
        : modifiedRecord[key];

    return originalValue !== modifiedValue;
  };

  // Function to format values for display
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    // Format monetary values
    if (
      ["totalAfe", "capexYear", "spesoProgressivo"].includes(key) &&
      typeof value === "number"
    ) {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    }

    return value.toString();
  };

  // Get the keys to display from the record
  const getKeysToDisplay = (record) => {
    if (!record) return [];

    // Define keys to display in the order you want
    const orderedKeys = [
      "afeCode",
      "afeDesc",
      "afeFyscalYear",
      "fiscalYearClosure",
      "fiscalYearRecord",
      "responsible",
      "programId",
      "userResponsible",
      "companyCode",
      "ou",
      "localKPI",
      "catAfe",
      "totalAfe",
      "capexYear",
      "spesoProgressivo",
      "localCorporate",
      "raggruppamento1",
      "raggruppamento2",
      "raggruppamento3",
    ];

    // Filter out keys that don't exist in the record
    return orderedKeys.filter((key) => record.hasOwnProperty(key));
  };

  // Function to get a more user-friendly field name
  const getFieldDisplayName = (key) => {
    const fieldNameMap = {
      afeCode: "Codice AFE",
      afeDesc: "Descrizione",
      afeFyscalYear: "Anno fiscale",
      fiscalYearClosure: "Anno di chiusura",
      fiscalYearRecord: "Record anno fiscale",
      responsible: "Responsabile",
      programId: "ID Programma",
      userResponsible: "Utente responsabile",
      companyCode: "Codice Azienda",
      ou: "OU",
      localKPI: "Local/KPI Approval",
      catAfe: "Categoria",
      totalAfe: "Totale AFE",
      capexYear: "Anno CAPEX",
      spesoProgressivo: "Speso Progressivo",
      localCorporate: "Local/Corporate",
      raggruppamento1: "Raggruppamento 1",
      raggruppamento2: "Raggruppamento 2",
      raggruppamento3: "Raggruppamento 3",
    };

    return fieldNameMap[key] || key;
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Get status chip color
  const getStatusChipText = (status) => {
    switch (status) {
      case "approved":
        return "Approvato";
      case "rejected":
        return "Rifiutato";
      case "pending":
        return "In attesa";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box
      sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}
    >
      <Q8Breadcrumbs
        links={[
          { label: "CAPEX", href: "/capex" },
          { label: "Storico modifiche", href: "/capex/history" },
        ]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            AFE Storico Modifiche
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Q8Button
              variant="secondary"
              label={filtersVisible ? "Nascondi Filtri" : "Mostra Filtri"}
              onClick={() => setFiltersVisible(!filtersVisible)}
              icon={<FilterIcon />}
              size="small"
            />
            <Q8Button
              variant="secondary"
              label="Aggiorna Dati"
              onClick={loadChangeHistory}
              icon={<RefreshIcon />}
              size="small"
            />
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mt: 1 }}>
          Vista dello storico modifiche per gli AFE records.
        </Typography>

        {filtersVisible && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Stato"
                  value={filters.status}
                  onChange={handleFilterChange("status")}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="approved">Approvati</MenuItem>
                  <MenuItem value="rejected">Rifiutati</MenuItem>
                  <MenuItem value="pending">In attesa</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="Da data"
                  value={filters.dateFrom}
                  onChange={handleFilterChange("dateFrom")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="A data"
                  value={filters.dateTo}
                  onChange={handleFilterChange("dateTo")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="AFE Code"
                  value={filters.code}
                  onChange={handleFilterChange("code")}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
                <b>ID</b>
              </TableCell>
              <TableCell>
                <b>Codice AFE</b>
              </TableCell>
              <TableCell>
                <b>Modificato da</b>
              </TableCell>
              <TableCell>
                <b>Modificato il</b>
              </TableCell>
              <TableCell>
                <b>Stato</b>
              </TableCell>
              <TableCell>
                <b>Gestito da</b>
              </TableCell>
              <TableCell>
                <b>Gestito il</b>
              </TableCell>
              <TableCell>
                <b>Note</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changeHistory.map((change) => (
              <React.Fragment key={change.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpandRecord(change.id)}
                    >
                      {expandedRecords[change.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{change.id}</TableCell>
                  <TableCell>{change.afeCode}</TableCell>
                  <TableCell>{change.modifiedBy}</TableCell>
                  <TableCell>{formatDate(change.modifiedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusChipText(change.status)}
                      color={getStatusChipColor(change.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{change.handledBy || "-"}</TableCell>
                  <TableCell>{formatDate(change.handledAt)}</TableCell>
                  <TableCell>{change.notes || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={9}
                  >
                    <Collapse
                      in={expandedRecords[change.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ m: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Dettagli modifica per AFE {change.afeCode}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <b>Campo</b>
                              </TableCell>
                              <TableCell>
                                <b>Valore originale</b>
                              </TableCell>
                              <TableCell>
                                <b>Valore modificato</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getKeysToDisplay(change.originalRecord).map(
                              (key) => {
                                const isDiff = isDifferent(
                                  key,
                                  change.originalRecord,
                                  change.modifiedRecord
                                );
                                return (
                                  <TableRow
                                    key={key}
                                    sx={{
                                      backgroundColor: isDiff
                                        ? "rgba(255, 243, 224, 0.5)"
                                        : "inherit",
                                    }}
                                  >
                                    <TableCell>
                                      {getFieldDisplayName(key)}
                                    </TableCell>
                                    <TableCell>
                                      {formatValue(
                                        key,
                                        change.originalRecord[key]
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: isDiff ? "bold" : "normal",
                                        color: isDiff
                                          ? "primary.main"
                                          : "inherit",
                                      }}
                                    >
                                      {formatValue(
                                        key,
                                        change.modifiedRecord[key]
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            )}
                            {getKeysToDisplay(change.originalRecord).length ===
                              0 && (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  Nessuna differenza trovata o dati incompleti
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {changeHistory.length === 0 && <Q8NoData />}
      </TableContainer>

      {/* Snackbar for notifications */}
      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AfeChangeHistory;
